import { getEnv } from '@/env';
import { OrganizationsService } from '@/services/organizationsService';
import { UserService } from '@/services/userService';
import { WorkspacesService } from '@/services/workspacesService';
import { useCanCreateOrganizationStore } from '@/stores/canCreateOrganizationStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useWorkspaceIdStore } from '@/stores/workspaceIdStore';
import { IndexDB } from '@/utils/indexDB';
import { LoaderFunctionArgs } from 'react-router-dom';

export async function setUserData() {
  const [userId, isEmailVerified, email] = await Promise.all([
    IndexDB.getUserId(),
    IndexDB.isEmailVerified(),
    IndexDB.getUserEmail(),
  ]);
  useUserPermissionsStore.getState().setId(userId || null);
  useUserPermissionsStore.getState().setEmail(email || null);
  useUserPermissionsStore.getState().setIsEmailVerified(isEmailVerified || null);
}

export async function setWorkspacePermission(args: LoaderFunctionArgs) {
  const currentWorkspacePermission =
    useUserPermissionsStore.getState().workspacePermission;
  const url = new URL(args.request.url);
  const workspaceId = url.searchParams.get('workspaceId');
  const userId = await IndexDB.getUserId();

  if (!workspaceId) {
    throw new Error('Workspace id is not provided');
  }

  if (currentWorkspacePermission?.workspaceId !== workspaceId) {
    const newWorkspacePermission = await WorkspacesService.getUserPermissions(
      workspaceId,
      userId as string,
    );
    useUserPermissionsStore.getState().setWorkspacePermission(newWorkspacePermission);
  }
}

export async function setOrganizationPermission() {
  const currentOrganizationPermission =
    useUserPermissionsStore.getState().organizationPermission;
  const organization = useOrganizationStore.getState().selectedOrganization;
  if (organization && !organization.isOrganizationMember) {
    useUserPermissionsStore.getState().setOrganizationPermission(null);
    return;
  }
  const organizationId = organization?.id;
  const userId = await IndexDB.getUserId();

  if (!organizationId) {
    throw new Error('Organization id is not provided');
  }

  if (currentOrganizationPermission?.organizationId !== organizationId) {
    const newOrganizationPermission = await OrganizationsService.getUserPermission(
      organizationId,
      userId as string,
    );
    useUserPermissionsStore
      .getState()
      .setOrganizationPermission(newOrganizationPermission);
  }
}

export async function setCanUserCreateOrganizations() {
  const userId = await IndexDB.getUserId();
  const isOrganizationCreationWhitelisted =
    getEnv()._VITE_IS_ORGANIZATION_CREATION_WHITELISTED === 'true';
  if (isOrganizationCreationWhitelisted) {
    const user = await UserService.get(userId as string);
    useCanCreateOrganizationStore
      .getState()
      .setCanCreateOrganization(!!user.canCreateOrganizations);
  }
}

export async function setWorkspaceId(args: LoaderFunctionArgs) {
  const url = new URL(args.request.url);
  const workspaceId = url.searchParams.get('workspaceId');
  const userId = await IndexDB.getUserId();
  if (workspaceId && userId) {
    useWorkspaceIdStore.getState().setWorkspaceId(workspaceId);
  }
}
